import { useCallback, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { useFleetApi, type ActionStep, type EscalationOption, type EscalationRow } from "./api";

// Derive a human-readable label for the classification chip
function classLabel(sc: string, dc: string): string {
  return [sc, dc].filter(Boolean).join(" / ").replace(/_/g, " ");
}

// Role of the agent (watcher / operator / specialist) derived from agent name heuristic
function agentRole(agent: string): "watcher" | "operator" | "specialist" {
  const a = agent.toLowerCase();
  if (a.includes("sentinel") || a.includes("watcher") || a.includes("drift") || a.includes("market")) return "watcher";
  if (a.includes("detective") || a.includes("investigator")) return "specialist";
  return "operator";
}

// Parse situation JSON for customer/order fields if present
function parseSituation(row: EscalationRow): { customer?: string; order?: string; proposed?: string } {
  const sk = row.situation_key ?? "";
  const order = sk.match(/SH-[\w\d]+/)?.[0] ?? sk.match(/order[_:](\S+)/i)?.[1];
  const customer = sk.match(/gmail\.thread:/) ? undefined : sk.match(/@[\w.]+/)?.[0];
  // Build a flat "key: scalar" preview from proposed_payload. Nested objects
  // are JSON-stringified so we don't render "[object Object]".
  let proposed: string | undefined;
  try {
    const pp = row.proposed_payload as Record<string, unknown>;
    if (pp && typeof pp === "object") {
      const vals = Object.entries(pp)
        .slice(0, 4)
        .map(([k, v]) => `${k}: ${formatScalar(v)}`)
        .join(" · ");
      if (vals) proposed = vals;
    }
  } catch {}
  return { customer, order, proposed };
}

function formatScalar(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    const s = String(v);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  }
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  } catch {
    return "(unrepresentable)";
  }
}

/** Legacy agent cards may still carry "Escalate to Arthur…" — map to reject. */
function normalizeOption(opt: EscalationOption): EscalationOption {
  if (/escalate to arthur/i.test(opt.label)) {
    return { ...opt, label: "Reject — no customer contact", recommended: false, verdict: "reject" };
  }
  return opt;
}

// Age in "Xh ago" or "Xm ago"
export function ageLabel(created_at: string): string {
  const diff = Date.now() - new Date(created_at).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `filed ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `filed ${hrs}h ago`;
}

interface CardProps {
  row: EscalationRow;
  onResolved: () => void;
}

export default function EscalationCard({ row, onResolved }: CardProps) {
  const { token, logout, user } = useAuth();
  const { resolveEscalation } = useFleetApi(token, logout);
  const actor = user?.username ?? user?.email ?? "operator";

  const [sliding, setSliding] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [payloadErr, setPayloadErr] = useState<
    | {
        action: string;
        missing: string[];
        payload_keys: string[];
        step?: number;
        completed_steps?: Array<{ step: number; action: string; status: string }>;
      }
    | null
  >(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const role = agentRole(row.agent);
  const { customer, order, proposed } = parseSituation(row);
  const why = (row.situation_class + " → " + row.decision_class).replace(/_/g, " ");

  const doResolve = useCallback(
    async (
      verdict: string,
      refinePayload?: Record<string, unknown>,
      optionIndex?: number,
    ) => {
      setBusy(true);
      setErr(null);
      setPayloadErr(null);
      try {
        await resolveEscalation(row.id, {
          verdict,
          actor,
          refine_payload: refinePayload,
          refine_text: customText || undefined,
          option_index: optionIndex,
        });
        setSliding(true);
        setTimeout(onResolved, 400);
      } catch (e) {
        // Try to surface a structured 422 (action_payload_invalid). apiFetch
        // attaches the parsed `detail` body to the Error instance; the
        // resolveEscalation wrapper may also throw `Error("resolve failed: N <text>")`
        // with the JSON body inlined as text.
        let handled = false;
        type PayloadErrorDetail = {
          code?: string;
          action?: string;
          failed_action?: string;
          missing?: unknown;
          payload_keys?: unknown;
          step?: unknown;
          completed_steps?: unknown;
        };
        const errAny = e as { detail?: PayloadErrorDetail; message?: string };
        const applyDetail = (detail: PayloadErrorDetail | undefined): boolean => {
          if (detail && detail.code === "action_payload_invalid") {
            const completedRaw = Array.isArray(detail.completed_steps) ? detail.completed_steps : [];
            const completed = completedRaw
              .map(s => {
                if (s && typeof s === "object") {
                  const so = s as Record<string, unknown>;
                  return {
                    step: Number(so.step ?? 0),
                    action: String(so.action ?? ""),
                    status: String(so.status ?? ""),
                  };
                }
                return null;
              })
              .filter((s): s is { step: number; action: string; status: string } => s !== null);
            setPayloadErr({
              action: String(detail.failed_action ?? detail.action ?? "(unknown)"),
              missing: Array.isArray(detail.missing) ? detail.missing.map(String) : [],
              payload_keys: Array.isArray(detail.payload_keys) ? detail.payload_keys.map(String) : [],
              step: typeof detail.step === "number" ? detail.step : undefined,
              completed_steps: completed,
            });
            return true;
          }
          return false;
        };
        if (errAny.detail) {
          handled = applyDetail(errAny.detail);
        }
        const raw = e instanceof Error ? e.message : String(e);
        if (!handled) {
          try {
            const jsonStart = raw.indexOf("{");
            if (jsonStart !== -1) {
              const parsed = JSON.parse(raw.slice(jsonStart));
              const detail = parsed?.detail ?? parsed;
              handled = applyDetail(detail);
            }
          } catch {
            // fall through to unstructured error
          }
        }
        if (!handled) {
          setErr(raw);
        }
        setBusy(false);
      }
    },
    [resolveEscalation, row.id, customText, onResolved, actor],
  );

  const handleOption = (opt: EscalationOption, idx: number) => {
    if (busy) return;
    if (opt.verdict === "reject") {
      doResolve("reject", undefined, idx);
      return;
    }
    const verdict = opt.recommended ? "approve_verbatim" : "approve_with_refine";
    // For compound options (opt.actions present), the backend reads
    // option.actions directly; we don't pass refine_payload — that would
    // collapse the multi-step dispatch into a single overridden first step.
    const refine = opt.actions && opt.actions.length > 0 ? undefined : opt.payload_override;
    doResolve(opt.verdict ?? verdict, refine, idx);
  };

  const handleReject = () => {
    if (busy) return;
    doResolve("reject");
  };

  const handleOverride = () => {
    if (busy) return;
    doResolve("override_approve");
  };

  const handleCustomSend = () => {
    if (!customText.trim() || busy) return;
    doResolve("approve_with_refine", { refine_text: customText });
  };

  const options = (Array.isArray(row.options) ? row.options : []).map(normalizeOption);
  const reasoningLines = row.reasoning.split("\n").filter(Boolean);
  const short = reasoningLines.slice(0, 3).join("\n");
  const isLong = reasoningLines.length > 3;

  return (
    <div
      ref={cardRef}
      className="esc"
      style={{
        transition: "opacity 0.35s, transform 0.35s",
        opacity: sliding ? 0 : 1,
        transform: sliding ? "translateX(40px)" : "none",
        overflow: "hidden",
      }}
    >
      <div className="esc-head">
        <h3>
          #E-{row.id}
          {" · "}
          <span className="pill muted" style={{ fontWeight: 500, fontSize: "11px" }}>
            {classLabel(row.situation_class, row.decision_class)}
          </span>
        </h3>
        <div className="meta">{ageLabel(row.created_at)}</div>
      </div>

      <div className="field">
        <div className="lbl">Agent</div>
        <div>
          <span className={`pill ${role}`}>{row.agent.replace(/_/g, " ")}</span>
          {row.is_test && (
            <span className="pill muted" style={{ marginLeft: "0.35rem" }}>
              test traffic
            </span>
          )}
        </div>
      </div>

      {customer && (
        <div className="field">
          <div className="lbl">Customer</div>
          <div>{customer}</div>
        </div>
      )}

      {order && (
        <div className="field">
          <div className="lbl">Order</div>
          <div>{order}</div>
        </div>
      )}

      {proposed && !order && !customer && (
        <div className="field">
          <div className="lbl">Proposed</div>
          <div>{proposed}</div>
        </div>
      )}

      <div className="field">
        <div className="lbl">Why escalated</div>
        <div>{why}</div>
      </div>

      <div className="body" style={{ position: "relative" }}>
        <span style={{ whiteSpace: "pre-wrap" }}>{expanded ? row.reasoning : short}</span>
        {isLong && (
          <button
            onClick={() => setExpanded(x => !x)}
            style={{
              background: "none",
              border: "none",
              color: "var(--info)",
              fontSize: "11.5px",
              cursor: "pointer",
              padding: "0 0 0 6px",
            }}
          >
            {expanded ? "collapse" : "…expand"}
          </button>
        )}
      </div>

      {err && (
        <div style={{ color: "var(--crit)", fontSize: "11.5px", margin: "6px 0" }}>
          {err}
        </div>
      )}

      {payloadErr && (
        <div
          style={{
            border: "1px solid var(--crit)",
            background: "rgba(180, 30, 30, 0.06)",
            borderRadius: "5px",
            padding: "8px 10px",
            margin: "6px 0",
            fontSize: "11.5px",
            color: "var(--crit)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            Action payload invalid
          </div>
          {payloadErr.completed_steps && payloadErr.completed_steps.length > 0 && (
            <div style={{ marginBottom: "4px" }}>
              Completed:{" "}
              {payloadErr.completed_steps
                .map(s => `${s.action} ${s.status.toUpperCase()}`)
                .join(", ")}
              .
            </div>
          )}
          <div>
            {typeof payloadErr.step === "number"
              ? `Failed at step ${payloadErr.step + 1} (${payloadErr.action})`
              : `Action: ${payloadErr.action}`}
          </div>
          <div>
            Missing fields:{" "}
            {payloadErr.missing.length > 0 ? payloadErr.missing.join(", ") : "(none reported)"}
          </div>
          <div>
            Payload had:{" "}
            {payloadErr.payload_keys.length > 0 ? payloadErr.payload_keys.join(", ") : "(empty)"}
          </div>
        </div>
      )}

      {customMode ? (
        <div style={{ marginTop: "10px" }}>
          <textarea
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            placeholder="Write your reply..."
            rows={4}
            style={{
              width: "100%",
              fontSize: "12.5px",
              fontFamily: "inherit",
              padding: "8px 10px",
              border: "1px solid var(--border-strong)",
              borderRadius: "5px",
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            <button
              className="opt recommended"
              disabled={busy || !customText.trim()}
              onClick={handleCustomSend}
            >
              Send
            </button>
            <button className="opt" disabled={busy} onClick={() => { setCustomMode(false); setCustomText(""); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="options">
          {options.map((opt, idx) => {
            const compoundActions: ActionStep[] = Array.isArray(opt.actions) ? opt.actions : [];
            return (
              <button
                key={idx}
                className={`opt${opt.recommended ? " recommended" : ""}`}
                disabled={busy}
                onClick={() => handleOption(opt, idx)}
                style={
                  compoundActions.length > 0
                    ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }
                    : undefined
                }
              >
                <span>{opt.label}</span>
                {compoundActions.length > 0 && (
                  <span
                    style={{
                      display: "flex",
                      gap: "4px",
                      flexWrap: "wrap",
                      marginTop: "2px",
                    }}
                  >
                    {compoundActions.map((a, i) => (
                      <span
                        key={i}
                        style={{
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          background: "var(--surface-2)",
                          color: "var(--text-2)",
                          fontWeight: 500,
                        }}
                      >
                        {String(a.action)}
                      </span>
                    ))}
                  </span>
                )}
              </button>
            );
          })}
          <button
            className="opt"
            disabled={busy}
            onClick={() => setCustomMode(true)}
          >
            Reply with my own text
          </button>
          <button
            className="opt"
            disabled={busy}
            onClick={handleReject}
          >
            Reject — no action
          </button>
          <button
            className="opt crit"
            style={{ marginLeft: "auto" }}
            disabled={busy}
            onClick={handleOverride}
            title="Approve even when bounds blocked this proposal"
          >
            Force approve (override bounds)
          </button>
        </div>
      )}
    </div>
  );
}
